﻿import React, { useState, useEffect, useRef} from "react";
import {
  fetchData,
  checkPredictionAvailability,
  detectDeviceType,
} from "./commonHelpers";
import axios from 'axios'
import usePortal from "react-useportal";
import { updateCurrentPlayer, createRoster, setPredictionId, selectView, updateUrl } from "./actions";

import throttle from 'lodash/fp/throttle';
import t from 'lodash.throttle'
import { useDispatch, batch} from "react-redux";
import ScrollAnimations from "./scrollAnimations";

function useScrollSpy ({
  activeSectionDefault = 0,
  offsetPx = 0,
  sectionElementRefs = [],
  throttleMs = 100,
}) {
  const [activeSection, setActiveSection] = useState(activeSectionDefault);

  const handle = throttle(throttleMs, () => {
    let currentSectionId = activeSection;
    for (let i = 0; i < sectionElementRefs.length; i++) {
      const section = sectionElementRefs[i].current;
      // Needs to be a valid DOM Element
      if (!section || !(section instanceof Element)) continue;
      // GetBoundingClientRect returns values relative to viewport
      if (section.getBoundingClientRect().top + offsetPx < 0) {
        currentSectionId = i;
        continue;
      }
      // No need to continue loop, if last element has been detected
      break;
    }

    setActiveSection(currentSectionId);
  });

  useEffect(() => {
    window.addEventListener('scroll', handle);

    return () => {
      window.removeEventListener('scroll', handle);
    };
  }, [sectionElementRefs, offsetPx]);
  return activeSection;
};

export function useValidation(keyArray = [], dataCapture, toggleModal) {
  const [value, setValue] = useState(setInitialValue(dataCapture));
  const [isValidated, setValidation] = useState(false);

  function setInitialValue(dataCapture) {
    if (dataCapture.location && dataCapture.teams) {
      return dataCapture
    }
    else {
      return keyArray.reduce((acc, item) => {
        return { ...acc, [item]: null };
      }, {})
    }
  }

  useEffect(() => {
    if (dataCapture.location && dataCapture.teams) {
      setValidation(true);
    } else {
      setValidation(value && Object.keys(value).every(i => value[i]));
    }
  }, [value, dataCapture]);

  const toggleSelections = item => {
    setValue({
      ...value,
      [item.key]: item.title
    });
    if (item.key !== "teams" && toggleModal) {
      toggleModal(showModal => !showModal);
    }
  };

  return { value, validatedData: isValidated && value, toggleSelections };
}

export function usePositionCreator(keyArray = [], initialPosition = null) {
  const [availablePositions, setAvailablePositions] = useState(null);
  const [selectedPosition, setSelectedPosition] = useState(initialPosition);

  const positions =
    keyArray &&
    keyArray
      .filter(
        position =>
          position.url && position.predictionAvailable.predictionIsLive
      )
      .map(item => {
        if (
          initialPosition &&
          initialPosition.positionShort === item.positionShort
        ) {
          return {
            ...item,
            isSelected: true
          };
        }
        return {
          ...item,
          isSelected: false
        };
      });

  useEffect(() => {
    setAvailablePositions(positions);
  }, [keyArray]);

  const togglePosition = selectedItem => {
    setAvailablePositions(
      availablePositions.map((item, index) => {
        if (selectedItem.positionShort !== item.positionShort) {
          return {
            ...item,
            isSelected: false
          };
        }

        const selectedPosition = {
          ...item,
          isSelected: true
        };

        setSelectedPosition(
          keyArray.find(
            position => position.positionShort === item.positionShort
          )
        );

        return selectedPosition;
      })
    );
  };

  return { availablePositions, togglePosition, selectedPosition };
}

export function useRosterNavigation(props) {
  const currentPosition = null;
  const togglePosition = null;

  return { currentPosition, togglePosition };
}

export function useMaxCount(maxCount) {
  const [isMaxSelection, setIsMaxSelection] = useState(false);

  return {
    isMaxSelection,
    setIsMaxSelection,
    maxCount
  };
}

export function useSelectionCounter(initial = 0, number = 8, setSelected) {
  const [selectionCount, setSelectionCount] = useState(initial);
  const { isMaxSelection, setIsMaxSelection, maxCount } = useMaxCount(number);

  const cardCounter = (counter, entityId) => {
    counter === maxCount ? setIsMaxSelection(true) : setIsMaxSelection(false);

    setSelected(selections => {
      const selectionCheck = selections.find(({ id }) => {
        return id === entityId;
      });

      if (selectionCheck) {
        return selections
          .filter(({ id }) => id !== entityId)
          .map((selection, index) => {
            return { ...selection, counter: index + 1 };
          });
      } else {
        return isMaxSelection
          ? selections
          : [...selections, { id: entityId, counter }];
      }
    });

    setSelectionCount(counter);
  };

  return { selectionCount, isMaxSelection, maxCount, cardCounter };
}

export function useOnClickOutside(ref, handler) {
  useEffect(
    () => {
      const listener = event => {
        // Do nothing if clicking ref's element or descendent elements
        if (!ref.current || ref.current.contains(event.target)) {
          return;
        }

        handler(event);
      };

      document.addEventListener("mousedown", listener);
      document.addEventListener("touchstart", listener);

      return () => {
        document.removeEventListener("mousedown", listener);
        document.removeEventListener("touchstart", listener);
      };
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [ref, handler]
  );
}

export function childrenWithProps(props) {
  return props && props.children
    ? React.Children.map(props.children, child => {
      const { children, ...otherProps } = props;
      return React.cloneElement(child, otherProps);
    })
    : null;
}

export function useToggleCard(cardCounter) {
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(false);

  const toggleCard = ({ selectionCount, isMaxSelection, entityId }) => {
    if (isMaxSelection) {
      cardCounter(selected ? --selectionCount : selectionCount, entityId);
      setSelected(false);
    } else {
      if (selected) {
        cardCounter(--selectionCount, entityId);
      } else {
        cardCounter(++selectionCount, entityId);
        dispatch(updateCurrentPlayer(entityId));
      }
      setSelected(selected => !selected);
    }
  };
  return {
    selected,
    toggleCard
  };
}

export function useSelected(initial, sectionRefs) {
  const activeSection = useScrollSpy({
    sectionElementRefs: sectionRefs,
    offsetPx: -140
  });
  const [selected, setSelected] = useState(initial);

  useEffect(() => {
    setSelected({ key: activeSection, type: "scroll" });
  }, [activeSection]);

  const onSelected = (key, type = "click") => {
    setSelected({ key, type });
  };
  return { selected, onSelected };
}

export const useModal = (baseClass, isDesktop = true) => {
 
  const desktop = `
        z-index: 9999;
        position: fixed;
        left: 50%;
        top: 50%;
        transform: translate(-50%,-50%);
      `
  const mobile = `
        z-index: 9999;
      `


  const { isOpen, togglePortal, closePortal, Portal} = usePortal({
    onOpen({ portal }) {
      const body = document.querySelector('body')
      const wrapDom = document.querySelector('.d3-l-wrap')
      wrapDom.classList.add('fade')

      !isDesktop ? body.className = "fixed" : "";

      portal.current.style.cssText = !isDesktop ? mobile : desktop;
      portal.current.className = `${baseClass}__modal`;

    },
    onClose({ portal }) {
      const body = document.querySelector('body')
      body.className = ""
      const wrapDom = document.querySelector('.d3-l-wrap')
      wrapDom.classList.remove('fade')
    }
  });

  return {
    Modal: Portal,
    openModal: togglePortal,
    closeModal: closePortal,
    isOpen
  };
};

export function useRosterFetch(rosterData) {
  const dispatch = useDispatch();

  useEffect(() => {
    const formatPositionDataAsync = item => {
      return fetchData(item.url)
        .then(res => {
          const { data, error } = res

          if (!error) {
            const { fields, thumbnail, references } = data || {};
            const { players } = references || [];

            const { startTime, endTime, maxCount, allTimeTeamSelection } =
              fields || {};

            const predictionAvailable = checkPredictionAvailability({
              startTime,
              endTime
            });

            const updatedItem = {
              ...item,
              predictionAvailable,
              maxCount,
              players,
              thumbnail
            };

            return updatedItem;
          }

          return item;
        })
    }

    const getData = () => {
      return Promise.all(
        rosterData.map(item => {
            if (item && item.url) {
              return formatPositionDataAsync(item)
                .then(res => res)
                .catch(err => console.error("formatPosition error", err))
            }
          }
        )
      )
        .then(res => dispatch(createRoster(res)))
        .catch(err => console.error("rosterData fetch Error", err));
    };

    getData();
  }, [rosterData]);
}

export const useGetData = (url, config, reRenderDependency = []) => {
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchData(url, config).then(res => {
      const { data } = res
      setResponse(data)
    }).catch(err => {
      const { error } = err
      setError(error)
    });  
  }, reRenderDependency);
  return { response, error };
};

export function useCarouselSettings(config) {
  const [withoutControls, setWithoutControls] = useState(
    (config && config.withoutControls) || false
  );
  const [transitionMode, setTransitionMode] = useState(
    (config && config.transitionMode) || "scroll"
  );
  const [cellAlign, setCellAlign] = useState(
    (config && config.cellAlign) || "center"
  );
  const [wrapAround, setWrapAround] = useState(
    (config && config.wrapAround) || true
  );
  const [underlineHeader, setUnderlineHeader] = useState(
    (config && config.underlineHeader) || false
  );
  const [heightMode, setHeightMode] = useState(
    (config && config.heightMode) || "max"
  );
  const [slidesToShow, setSlidesToShow] = useState(
    (config && config.slidesToShow) || 1.25
  );
  const [slideIndex, setSlideIndex] = useState(
    (config && config.slideIndex) || 0
  );

  const [framePadding, setFramePadding] = useState(
    (config && config.framePadding) || '0'
  )

  const [width, setWidth] = useState(
    (config && config.width) || '100%'
  )


  const carouselSettings = {
    withoutControls,
    transitionMode,
    cellAlign,
    wrapAround,
    underlineHeader,
    heightMode,
    slidesToShow,
    framePadding,
    width
  };

  return { carouselSettings };
}

export function useIsMobile(initial) {
  const [isMobile, setIsMobile] = useState(initial);

  useEffect(() => {
    window.addEventListener(
      "resize",

      t(() => {
        setIsMobile(detectDeviceType() === "Mobile")
      }),
      500
    );

    return () => {
      window.removeEventListener(
        "resize",
        t(() => {
          setIsMobile(detectDeviceType() === "Mobile")
        }),
        500
      );
    };
  }, []);


  return isMobile;
}

export const getMobileDetect = userAgent => {
  const isAndroid = () => Boolean(userAgent.match(/Android/i));
  const isIos = () => Boolean(userAgent.match(/iPhone|iPad|iPod/i));
  const isOpera = () => Boolean(userAgent.match(/Opera Mini/i));
  const isWindows = () => Boolean(userAgent.match(/IEMobile/i));

  const isMobile = () =>
    Boolean(isAndroid() || isIos() || isOpera() || isWindows());
  const isDesktop = () => !isMobile();
  return {
    isMobile,
    isDesktop,
    isAndroid,
    isIos
  };
};


export function sendDataToDb(currentUrl, data, feUrl, postApi = "/api/prediction/set") {

  return dispatch => {
    axios({
      method: 'post',
      headers: { 'content-type': 'application/json' },
      url: `${feUrl}${postApi}`,
      data: JSON.stringify(data)
    })
      .then(res => {
        const { predictionId, selectionId } = JSON.parse(res.data)
        if (predictionId || selectionId) {
          const id = predictionId || selectionId


          batch(() => {
            dispatch(setPredictionId(id))
            dispatch(selectView("SUBMISSIONS"))
            dispatch(updateUrl(currentUrl, 'submissions'))
          })
        }
      })
      .catch(error => {
        console.error(error, 'could not submit selections');

        batch(() => {
          dispatch(selectView("SUBMISSIONS"))
          dispatch(updateUrl(currentUrl, 'submissions'))
        })
      });
  }
}

export function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}


export function usePaneModal(isOpen = false) {
  const bodyDom = document.querySelector('body')
  const wrapDom = document.querySelector('.d3-l-wrap')
  const [ isPaneOpen, setIsPaneOpen ] = useState(isOpen)


  useEffect(() => {
    const documentWidth = document.documentElement.clientWidth;
    const windowWidth = window.innerWidth;
    const scrollBarWidth = windowWidth - documentWidth;

    if (isPaneOpen) {
      bodyDom.style.paddingRight = `${scrollBarWidth}px`;
      bodyDom.classList.add('fixed')
      wrapDom.style.opacity = 0.3;
    }

    else {
      bodyDom.style.paddingRight = 'initial'
      bodyDom.classList.remove('fixed')
      wrapDom.style.opacity = 'initial';
    }

  }, [isPaneOpen])

  return {isPaneOpen, setIsPaneOpen}
}

export const useHover = (ref, isMobile) => {
  const [isHovered, setIsHovered] = useState(false)
  const toggleHover = (e) => {
    if (e.pointerType !== "touch") {
      setIsHovered(isHovered => !isHovered)
    }
  }

  useEffect(() => {
    if (isMobile) {
      new ScrollAnimations({
        elements: [ref.current],
        isMobile: isMobile,
        isMoment: true
      });
    }
  }, [])

  return  { isHovered, toggleHover }
}